import { useCallback, useEffect, useRef, useState } from "react";

import type { StorageItems } from "$src/core/services/storage/storage";
import Storage from "$src/core/services/storage/storage";

const useStorageState = <K extends keyof StorageItems>(
  key: K,
  defaultValue: StorageItems[K] | null,
) => {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  const [state, _setState] = useState<StorageItems[K] | null>(() => {
    const value = Storage.get(key);
    if (value === null) return defaultValue;
    return value;
  });
  const stateRef = useRef(state);

  const setState = useCallback(
    (
      value:
        | (StorageItems[K] | null)
        | ((prev: StorageItems[K] | null) => StorageItems[K] | null),
    ) => {
      if (typeof value === "function") {
        _setState((prev) => {
          const newValue = value(prev);
          stateRef.current = newValue;
          return newValue;
        });
      } else {
        _setState(value ?? defaultValue);
        stateRef.current = value;
      }
    },
    [defaultValue],
  );

  /* Sync storage with the state */
  useEffect(() => {
    const currentValue = Storage.get(key);
    if (currentValue === state) return;

    if (state === null) {
      Storage.remove(key);
    } else {
      Storage.set(key, state);
    }
  }, [key, state]);

  useEffect(() => {
    const unsubscribe = Storage.subscribe(key, (newValue) => {
      const isEqual = () => {
        // For object/array values do a deep comparison using JSON.stringify
        if (typeof newValue === "object" && newValue !== null) {
          return JSON.stringify(newValue) === JSON.stringify(stateRef.current);
        }

        return newValue === stateRef.current;
      };

      if (!isEqual()) {
        setState(newValue);
      }
    });
    return unsubscribe;
  }, [defaultValue, key, setState]);

  return [state, setState] as const;
};

export default useStorageState;
